import { useState } from "react";
import { endpoints } from "../../hooks/endpoints";
import { convertText, getFileUrl, getToken } from "../../hooks/utils";
import Animation from "../Animation";
import QuestionImg from "../quiz/QuestionImg";
import Spinner from "../Spinner";

import { invokePortalRefetch } from "@/store/slices/storeSlice";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { storage } from "../../firebaseConfig";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { pushModal } from "../../store/slices/modalsSlice";
import GeneralMessageModal from "../Modals/GeneralMessageModal";
import Separator from "../Separator";
import { Button } from "../ui/button";
import AddFromQuestionBank from "./AddFromQuestionBank";
import FileUploadProgressBar from "./FileUploadProgressBar";

export interface QuesDataI {
  data: {
    id: string;
    title: string;
    allowed_trials: string;
    pass_grade: string;
    time_minutes: string;
    questions_ids: string;
    hide_correct_answers: boolean;
    reorder_questions: boolean;
    must_answer_all: boolean;
    description: string;
    last_trial_date: string;
  };
  questions_data: {
    id: string;
    image_url: string;
    question_hint: string;
    answers: string[];
    weight: number;
    correct_answer: string;
    answers_count: number;
    content: string;
  }[];
}

const EditQuiz = () => {
  const [assessmentType, setAssessmentType] = useState("");
  const [selectedAssessment, setSelectedAssessment] = useState("");
  const [assessmentData, setAssessmentData] = useState<QuesDataI>();

  const [isEditing, setIsEditing] = useState(false);
  const [isQuizLoadingPending, setIsQuizLoadingPending] = useState(false);
  const [fileProgress, setFileProgress] = useState(null);

  const { userProfileData } = useAppSelector((state) => state.store);
  const quizes = userProfileData?.hws_quizzes_lessons?.filter((item) =>
    ["quiz", "homework"].includes(item.post_type)
  );
  const dispatch = useAppDispatch();

  const handleGetQuizData = async (e) => {
    try {
      if (!e.target.value) return setAssessmentData(undefined);
      setIsQuizLoadingPending(true);

      const response = await fetch(
        `${endpoints.getAssessmentDataForEdit}?assessment_id=${e.target.value}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getToken(),
          },
          method: "GET",
        }
      );

      const res = await response.json();

      if (!response.ok) {
        throw Error(res.data.message || "حدث خطأ");
      }

      setAssessmentData(res);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsQuizLoadingPending(false);
    }
  };

  const handleUploadFb = (e, index) => {
    if (!e.target.files[0]) {
      dispatch(
        pushModal(<GeneralMessageModal msg="يجب اختيار صورة" btnMsg="حسنا" />)
      );
      return;
    }

    const imageRef = ref(storage, `questions-${new Date().getTime()}`);
    const uploadTask = uploadBytesResumable(imageRef, e.target.files[0]);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setFileProgress(progress.toFixed(2));
        console.log("Upload is " + progress + "% done");
      },
      (error) => {
        console.log(error);
        dispatch(
          pushModal(<GeneralMessageModal msg="حدث خطأ" btnMsg="حسنا" />)
        );
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setFileProgress(null);
          const url = getFileUrl(downloadURL);
          setAssessmentData((prev) => ({
            ...prev,
            questions_data: prev.questions_data.map((q, idx) => {
              if (idx === index) {
                return {
                  ...q,
                  image_url: url,
                };
              }
              return q;
            }),
          }));
        });
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(assessmentData);
    setIsEditing(true);
    try {
      const response = await fetch(endpoints.editQuiz, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        body: JSON.stringify(assessmentData),
      });

      if (!response.ok) {
        throw Error("Error submitting the form");
      } else {
        toast.success("تم التعديل");
        dispatch(invokePortalRefetch());
      }
    } catch (error) {
      toast.error("حدث خطأ");
      console.log(error);
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <Animation>
      <div className="space-y-6">
        <h3 className="bg-primary p-3 text-white rounded-lg shadow font-bold">
          تعديل إمتحان / واجب
        </h3>
        <form
          onSubmit={(e) => handleSubmit(e)}
          className="p-3 bg-white rounded-lg shadow-sm space-y-4"
        >
          <select
            onChange={(e) => {
              setAssessmentType(e.target.value);
              setSelectedAssessment("");
              setAssessmentData(undefined);
            }}
            className="block w-full px-3 py-2 mb-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-(--primary-color-light)"
          >
            <option value="" selected>
              نوع الإمتحان
            </option>
            <option value="quiz">كويز</option>
            <option value="homework">واجب</option>
          </select>

          {assessmentType !== "quizType" && (
            <select
              value={selectedAssessment}
              onChange={(e) => {
                setSelectedAssessment(e.target.value);
                handleGetQuizData(e);
              }}
              className="block w-full px-3 py-2 mb-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-(--primary-color-light)"
            >
              <option value="" selected>
                إختر الإمتحان
              </option>
              {quizes &&
                quizes
                  ?.filter((q) => q.post_type === assessmentType)
                  ?.map((quiz) => (
                    <option key={quiz.ID} value={quiz.ID}>
                      {convertText(quiz.post_title)}
                    </option>
                  ))}
            </select>
          )}

          {!isQuizLoadingPending && !!assessmentType && assessmentData?.data ? (
            <>
              <Separator />
              <div className="flex flex-col gap-2">
                <label htmlFor="title" className="block mb-1 font-medium">
                  عنوان الإمتحان
                </label>
                <input
                  value={assessmentData.data.title}
                  onChange={(e) =>
                    setAssessmentData((prev) => ({
                      ...prev,
                      data: {
                        ...prev.data,
                        title: e.target.value,
                      },
                    }))
                  }
                  type="text"
                  id="title"
                  name="title"
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary-color-light)"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="description" className="block font-medium">
                  رسالة قبل بدء الإمتحان
                </label>
                <input
                  type="text"
                  id="description"
                  name="description"
                  value={assessmentData?.data?.description}
                  onChange={(e) =>
                    setAssessmentData((prev) => ({
                      ...prev,
                      data: {
                        ...prev.data,
                        description: e.target.value,
                      },
                    }))
                  }
                  className="block w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>

              {assessmentType === "quiz" && (
                <div className="flex flex-col gap-2">
                  <label htmlFor="pass_score" className="block font-medium">
                    درجة النجاح
                  </label>
                  <input
                    value={assessmentData.data.pass_grade}
                    onChange={(e) =>
                      setAssessmentData((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          pass_grade: e.target.value,
                        },
                      }))
                    }
                    type="number"
                    id="pass_score"
                    name="pass_score"
                    className="block w-full border rounded-lg px-3 py-2"
                    min={0.5}
                    step={0.5}
                    required
                  />
                </div>
              )}
              {assessmentType === "quiz" && (
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="quiz_max_redo_count"
                    className="block font-medium"
                  >
                    عدد مرات دخول الامتحان للطالب
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={assessmentData.data.allowed_trials}
                    onChange={(e) =>
                      setAssessmentData((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          allowed_trials: e.target.value,
                        },
                      }))
                    }
                    id="quiz_max_redo_count"
                    name="quiz_max_redo_count"
                    className="block w-full border rounded-lg px-3 py-2"
                    required
                  />
                </div>
              )}

              {assessmentType === "quiz" && (
                <div className="flex flex-col gap-2">
                  <label htmlFor="timer" className="block mb-1 font-medium">
                    الوقت (بالدقائق)
                  </label>
                  <input
                    value={assessmentData.data.time_minutes}
                    onChange={(e) =>
                      setAssessmentData((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          time_minutes: e.target.value,
                        },
                      }))
                    }
                    type="number"
                    id="timer"
                    name="timer"
                    min={0}
                    max={360}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary-color-light)"
                  />
                </div>
              )}

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="last_trial_date"
                  className="block mb-1 font-medium"
                >
                  اخر ميعاد لدخول الإمتحان (إختياري)
                </label>
                <input
                  value={
                    assessmentData.data && assessmentData.data.last_trial_date
                      ? assessmentData.data.last_trial_date[0]
                      : ""
                  }
                  onChange={(e) =>
                    setAssessmentData((prev) => ({
                      ...prev,
                      data: {
                        ...prev.data,
                        last_trial_date: new Date(e.target.value)
                          .getTime()
                          .toString(),
                      },
                    }))
                  }
                  type="datetime-local"
                  id="last_trial_date"
                  name="last_trial_date"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary-color-light)"
                />
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-2">
                <input
                  onChange={(e) =>
                    setAssessmentData((prev) => ({
                      ...prev,
                      data: {
                        ...prev.data,
                        hide_correct_answers: e.target.checked,
                      },
                    }))
                  }
                  checked={assessmentData?.data?.hide_correct_answers}
                  type="checkbox"
                  id="hide_correct_answers"
                  className="form-checkbox size-4 text-primary"
                />
                <label htmlFor="hide_correct_answers" className="ml-2">
                  إخفاء الإجابات الصحيحة
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  onChange={(e) =>
                    setAssessmentData((prev) => ({
                      ...prev,
                      data: {
                        ...prev.data,
                        reorder_questions: e.target.checked,
                      },
                    }))
                  }
                  checked={assessmentData?.data?.reorder_questions}
                  type="checkbox"
                  id="randomize_questions"
                  className="form-checkbox size-4 text-primary"
                />
                <label htmlFor="randomize_questions" className="ml-2">
                  إعادة ترتيب الأسئلة لكل طالب
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  onChange={(e) =>
                    setAssessmentData((prev) => ({
                      ...prev,
                      data: {
                        ...prev.data,
                        must_answer_all: e.target.checked,
                      },
                    }))
                  }
                  checked={assessmentData?.data?.must_answer_all}
                  type="checkbox"
                  id="must_answer_all"
                  className="form-checkbox size-4 text-primary"
                />
                <label htmlFor="must_answer_all" className="ml-2">
                  يجب حل جميع الأسئلة
                </label>
              </div>

              {assessmentData.questions_data.map((question, index) => (
                <div
                  key={question.id}
                  className="my-6 space-y-4 pt-4 border-t border-black"
                >
                  <div className="flex gap-2">
                    <h5 className="mb-3 pb-2 w-fit border-b-2 border-primary font-bold">
                      سؤال رقم {index + 1}
                    </h5>
                    {assessmentData.questions_data.length > 1 && (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() =>
                          setAssessmentData((prev) => ({
                            ...prev,
                            questions_data: prev.questions_data.filter(
                              (q, i) => i !== index
                            ),
                          }))
                        }
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>

                  <AddFromQuestionBank
                    setAssessmentData={setAssessmentData}
                    questionNum={index}
                  />

                  <input
                    id={`qid_${index}`}
                    type="hidden"
                    name={`qid_${index}`}
                  />

                  {fileProgress && fileProgress < 100 && fileProgress >= 0 && (
                    <FileUploadProgressBar fileProgress={fileProgress} />
                  )}

                  {!question.image_url && (
                    <Button
                      type="button"
                      variant="outline"
                      className="hover:bg-purple-200 w-full h-fit py-5 rounded-lg bg-purple-100 border-2 border-dashed border-purple-500 flex flex-col gap-2 items-center justify-center"
                      onClick={() =>
                        document.getElementById(`img_input_${index}`)?.click()
                      }
                    >
                      <Upload className="size-10" />
                      <p className="no-underline">إرفع صورة السؤال</p>
                    </Button>
                  )}

                  <input
                    id={`img_input_${index}`}
                    onChange={(e) => handleUploadFb(e, index)}
                    data-id={question.id}
                    className="w-full border rounded-lg px-3 py-2"
                    type="file"
                    hidden
                    name={`quiz_question_img_input_${question.id}`}
                  />

                  {question.image_url && (
                    <QuestionImg
                      id={`uploaded-image-${question.id ?? index}`}
                      src={question.image_url}
                      alt=""
                      className="m-auto mb-2 rounded-lg"
                      border="2px solid #ddd"
                      onImgDelete={() => {
                        setAssessmentData((prev) => ({
                          ...prev,
                          questions_data: prev.questions_data.map((q, i) =>
                            i === index ? { ...q, image_url: "" } : q
                          ),
                        }));
                      }}
                    />
                  )}

                  <input
                    id={`quiz_question_img_url_${index}`}
                    value={question.image_url ?? ""}
                    type="hidden"
                    name={`quiz_question_img_url_${index}`}
                    required
                  />

                  <div className="mb-3">
                    <label
                      htmlFor={`quiz_question_textarea_${index}`}
                      className="block mb-1 font-medium"
                    >
                      محتوى السؤال (اختياري)
                    </label>
                    <textarea
                      id={`quiz_question_textarea_${index}`}
                      name={`quiz_question_textarea_${index}`}
                      rows={3}
                      className="block w-full border rounded-lg px-3 py-2"
                      value={question.content ?? ""}
                      onChange={(e) =>
                        setAssessmentData((prev) => ({
                          ...prev,
                          questions_data: prev.questions_data.map((q, i) =>
                            i === index ? { ...q, content: e.target.value } : q
                          ),
                        }))
                      }
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor={`quiz_question_ans_count_${index}`}
                      className="block mb-1 font-medium"
                    >
                      عدد الإجابات
                    </label>
                    <input
                      type="number"
                      id={`quiz_question_ans_count_${index}`}
                      name={`quiz_question_ans_count_${index}`}
                      className="block w-full border rounded-lg px-3 py-2"
                      max={10}
                      min={2}
                      required
                      value={question.answers_count ?? ""}
                      onChange={(e) =>
                        setAssessmentData((prev) => ({
                          ...prev,
                          questions_data: prev.questions_data.map((q, i) =>
                            i === index
                              ? { ...q, answers_count: +e.target.value }
                              : q
                          ),
                        }))
                      }
                    />
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor={`quiz_question_ans_${index}`}
                      className="block mb-1 font-medium"
                    >
                      الإجابة الصحيحة
                    </label>
                    <select
                      id={`quiz_question_ans_${index}`}
                      name={`quiz_question_ans_${index}`}
                      className="block w-full border rounded-lg px-3 py-2"
                      value={question.correct_answer ?? ""}
                      onChange={(e) =>
                        setAssessmentData((prev) => ({
                          ...prev,
                          questions_data: prev.questions_data.map((q, i) =>
                            i === index
                              ? { ...q, correct_answer: e.target.value }
                              : q
                          ),
                        }))
                      }
                      required
                    >
                      <option value="">الإجابة الصحيحة</option>
                      {["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"]
                        .slice(0, question.answers_count)
                        .map((ans) => (
                          <option key={ans} value={ans}>
                            {ans}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor={`quiz_question_weight_${index}`}
                      className="block mb-1 font-medium"
                    >
                      الدرجة
                    </label>
                    <input
                      type="number"
                      min={0.5}
                      step={0.5}
                      id={`quiz_question_weight_${index}`}
                      name={`quiz_question_weight_${index}`}
                      className="block w-full border rounded-lg px-3 py-2"
                      value={question.weight ?? ""}
                      onChange={(e) =>
                        setAssessmentData((prev) => ({
                          ...prev,
                          questions_data: prev.questions_data.map((q, i) =>
                            i === index ? { ...q, weight: +e.target.value } : q
                          ),
                        }))
                      }
                      required
                    />
                  </div>
                </div>
              ))}

              {/* Add/Remove question */}
              <div className="mb-3">
                <Button
                  type="button"
                  disabled={
                    fileProgress && fileProgress < 100 && fileProgress >= 0
                  }
                  onClick={() =>
                    setAssessmentData((prev) => ({
                      ...prev,
                      questions_data: [
                        ...prev.questions_data,
                        {
                          tempId: new Date().getTime(),
                          id: "",
                          image_url: "",
                          question_hint: "",
                          answers: [],
                          weight: 1,
                          correct_answer: "",
                          answers_count: 4,
                          content: "",
                        },
                      ],
                    }))
                  }
                  className="rounded-lg font-bold px-4 py-2 bg-green-600 hover:bg-green-700 text-white"
                >
                  +
                </Button>
              </div>

              <Button
                type="submit"
                className="w-full rounded-lg"
                disabled={
                  (typeof fileProgress === "number" && !isNaN(fileProgress)) ||
                  isEditing
                }
              >
                {isEditing ? <Spinner variant="button" /> : "حفظ التغييرات"}
              </Button>
            </>
          ) : isQuizLoadingPending ? (
            <div className="flex justify-center items-center grow">
              <Spinner />
            </div>
          ) : (
            ""
          )}
        </form>
      </div>
    </Animation>
  );
};

export default EditQuiz;
